import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calculator, Edit2, Plus, RefreshCw, Store, Tags, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const initialForm = {
  name: "",
  consignor_name: "",
  consignor_phone: "",
  category: "",
  price: "",
  stock: "",
  description: "",
  image: "",
};

const initialCategoryForm = {
  name: "",
  is_active: true,
};

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(amount || 0));

const resolveImageUrl = (image) => {
  if (!image) return "";
  if (String(image).startsWith("http://") || String(image).startsWith("https://")) return image;
  return `${BACKEND_URL}${image}`;
};

const getProductSortTime = (product) => {
  const parsed = Date.parse(product?.created_at || product?.updated_at || "");
  return Number.isNaN(parsed) ? 0 : parsed;
};

export default function ConsignmentProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [selectedExistingProductId, setSelectedExistingProductId] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false);
  const [selectedSettlementProduct, setSelectedSettlementProduct] = useState(null);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementSaving, setSettlementSaving] = useState(false);
  const [purchasePricePerPcs, setPurchasePricePerPcs] = useState("");
  const [settlementEvidenceFile, setSettlementEvidenceFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const activeCategories = useMemo(() => categories.filter((item) => item.is_active !== false), [categories]);
  const formCategoryOptions = useMemo(() => {
    if (!formData.category || activeCategories.some((item) => item.id === formData.category)) {
      return activeCategories;
    }
    const selectedCategory = categories.find((item) => item.id === formData.category);
    return selectedCategory ? [...activeCategories, selectedCategory] : activeCategories;
  }, [activeCategories, categories, formData.category]);
  const categoryMap = useMemo(
    () => categories.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {}),
    [categories]
  );
  const consignmentProducts = useMemo(
    () =>
      products
        .filter((product) => String(product.sale_type || "OWN").toUpperCase() === "CONSIGNMENT")
        .sort((a, b) => {
          const timeDiff = getProductSortTime(b) - getProductSortTime(a);
          if (timeDiff !== 0) return timeDiff;
          return String(a.name || "").localeCompare(String(b.name || ""));
        }),
    [products]
  );
  const visibleConsignmentProducts = useMemo(
    () =>
      consignmentProducts.filter((product) => {
        if (statusFilter === "active") return product.is_active !== false;
        if (statusFilter === "inactive") return product.is_active === false;
        return true;
      }),
    [consignmentProducts, statusFilter]
  );
  const uniqueConsignmentProducts = useMemo(() => {
    const seen = new Set();
    return consignmentProducts.filter((product) => {
      const key = String(product.name || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [consignmentProducts]);
  const selectedExistingProduct = useMemo(
    () => consignmentProducts.find((item) => item.id === selectedExistingProductId) || null,
    [consignmentProducts, selectedExistingProductId]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/consignment-categories`, { withCredentials: true }),
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    } catch (error) {
      console.error("Failed to fetch consignment products:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data produk titipan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setFormData(initialForm);
    setSelectedExistingProductId("");
    setSelectedImageFile(null);
    setIsModalOpen(true);
  };

  const handleProductNameSelect = (productId) => {
    setSelectedExistingProductId(productId);
    if (!productId) {
      setFormData(initialForm);
      setSelectedImageFile(null);
      return;
    }
    const product = consignmentProducts.find((item) => item.id === productId);
    if (!product) return;
    setFormData((prev) => ({
      ...prev,
      name: String(product.name || ""),
      category: String(product.category || prev.category || ""),
      consignor_name: String(product.consignor_name || prev.consignor_name || ""),
      consignor_phone: String(product.consignor_phone || prev.consignor_phone || ""),
      price: product.price !== undefined ? String(Number(product.price || 0)) : prev.price,
      image: String(product.image || prev.image || ""),
      description: String(product.description || prev.description || ""),
    }));
    setSelectedImageFile(null);
  };

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(initialCategoryForm);
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: String(category.name || ""),
      is_active: category.is_active !== false,
    });
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: categoryForm.name.trim(),
      is_active: !!categoryForm.is_active,
    };
    try {
      if (editingCategory) {
        await axios.put(`${API}/consignment-categories/${editingCategory.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/consignment-categories`, payload, { withCredentials: true });
      }
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      setCategoryForm(initialCategoryForm);
      await fetchData();
    } catch (error) {
      console.error("Failed to save consignment category:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan kategori titipan.");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Hapus kategori titipan ini?")) return;
    try {
      await axios.delete(`${API}/consignment-categories/${categoryId}`, { withCredentials: true });
      await fetchData();
    } catch (error) {
      console.error("Failed to delete consignment category:", error);
      alert(error.response?.data?.detail || "Gagal menghapus kategori titipan.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.category) {
      alert("Pilih kategori produk terlebih dulu.");
      return;
    }

    try {
      setSubmitting(true);
      let imagePath = formData.image.trim();
      if (selectedImageFile) {
        const uploadData = new FormData();
        uploadData.append("file", selectedImageFile);
        const uploadRes = await axios.post(`${API}/uploads/product-image`, uploadData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        imagePath = uploadRes.data?.path || imagePath;
      }
      if (!imagePath) {
        alert("Gambar produk wajib diupload.");
        return;
      }

      await axios.post(
        `${API}/products`,
        {
          name: formData.name.trim(),
          category: formData.category,
          price: Number(formData.price || 0),
          cost_price: 0,
          stock: Number(formData.stock || 0),
          image: imagePath,
          description: formData.description.trim() || "Produk titipan",
          sale_type: "CONSIGNMENT",
          consignor_name: formData.consignor_name.trim(),
          consignor_phone: formData.consignor_phone.trim(),
          is_active: true,
          sort_order: 0,
        },
        { withCredentials: true }
      );
      setIsModalOpen(false);
      setFormData(initialForm);
      setSelectedExistingProductId("");
      setSelectedImageFile(null);
      await fetchData();
    } catch (error) {
      console.error("Failed to create consignment product:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan produk titipan.");
    } finally {
      setSubmitting(false);
    }
  };

  const openSettlementModal = async (product) => {
    setSelectedSettlementProduct(product);
    setSettlementSummary(null);
    setPurchasePricePerPcs(product.cost_price ? String(Number(product.cost_price || 0)) : "");
    setSettlementEvidenceFile(null);
    setIsSettlementModalOpen(true);
    try {
      setSettlementLoading(true);
      const { data } = await axios.get(`${API}/products/${product.id}/consignment-summary`, {
        withCredentials: true,
      });
      setSettlementSummary(data || null);
      const productCostPrice = Number(data?.product?.cost_price || product.cost_price || 0);
      if (productCostPrice > 0) setPurchasePricePerPcs(String(productCostPrice));
    } catch (error) {
      console.error("Failed to fetch consignment settlement summary:", error);
      alert(error.response?.data?.detail || "Gagal mengambil rekap produk titipan.");
    } finally {
      setSettlementLoading(false);
    }
  };

  const settlementProduct = settlementSummary?.product || selectedSettlementProduct || {};
  const settlementSoldQuantity = Number(settlementSummary?.sold_quantity || 0);
  const settlementRemainingStock = Number(settlementSummary?.remaining_stock ?? settlementProduct.stock ?? 0);
  const settlementReturnedStock = Number(
    settlementSummary?.returned_stock ?? settlementProduct.consignment_settlement_remaining_stock ?? 0
  );
  const settlementSaved = Boolean(settlementProduct.consignment_settlement_saved);
  const settlementPurchasePrice = settlementSaved
    ? Number(settlementProduct.consignment_settlement_purchase_price ?? settlementProduct.cost_price ?? 0)
    : Number(purchasePricePerPcs || 0);
  const settlementPayable = settlementSoldQuantity * settlementPurchasePrice;

  const saveSettlement = async () => {
    if (!settlementProduct?.id) return;
    if (settlementSaved) return;
    if (settlementPurchasePrice < 0) {
      alert("Harga beli per pcs tidak valid.");
      return;
    }
    if (!window.confirm("Simpan rekap setoran? Untuk user kasir, data akan dikirim sebagai payment request ke admin terlebih dulu.")) return;
    try {
      setSettlementSaving(true);
      let evidencePath = "";
      if (settlementEvidenceFile) {
        const uploadData = new FormData();
        uploadData.append("file", settlementEvidenceFile);
        const uploadRes = await axios.post(`${API}/uploads/payment-request-evidence`, uploadData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        evidencePath = uploadRes.data?.path || "";
      }
      const { data } = await axios.post(
        `${API}/products/${settlementProduct.id}/consignment-settlement`,
        { purchase_price_per_pcs: settlementPurchasePrice, evidence_path: evidencePath },
        { withCredentials: true }
      );
      if (String(data?.status || "").toLowerCase() === "pending") {
        alert(data?.message || "Payment request rekap setoran berhasil dikirim ke admin.");
        setIsSettlementModalOpen(false);
        setSettlementEvidenceFile(null);
        await fetchData();
        return;
      }
      const updatedProduct = data?.product || null;
      setSettlementSummary((prev) => ({
        ...(prev || {}),
        product: updatedProduct || settlementProduct,
        sold_quantity: data?.sold_quantity ?? settlementSoldQuantity,
        remaining_stock: data?.remaining_stock ?? settlementRemainingStock,
        returned_stock: data?.returned_stock ?? settlementReturnedStock,
      }));
      if (updatedProduct) {
        setSelectedSettlementProduct(updatedProduct);
        setPurchasePricePerPcs(String(updatedProduct.consignment_settlement_purchase_price ?? updatedProduct.cost_price ?? settlementPurchasePrice));
      }
      await fetchData();
    } catch (error) {
      console.error("Failed to save consignment settlement:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan rekap setoran.");
    } finally {
      setSettlementSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Product Titipan" />
      <div className="space-y-6 p-8" data-testid="consignment-products-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A] hover:bg-[#D4AF37]/10">
                Konsinyasi
              </Badge>
              <h2 className="text-xl font-semibold text-white">Produk Titipan</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Barang titipan tetap bisa dijual di POS, tetapi perhitungan keuangannya dipisahkan dari produk gym.
              </p>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={fetchData} className="rounded-sm border-white/10 text-white hover:bg-zinc-800">
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button type="button" onClick={openCreateModal} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                <Plus className="mr-2 h-4 w-4" />
                Tambah Titipan
              </Button>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#F3D77A]">
                <Tags className="h-3.5 w-3.5" />
                Kategori Titipan
              </div>
              <p className="text-sm text-zinc-500">Kategori ini terpisah dari kategori Master Product.</p>
            </div>
            <Button type="button" onClick={openCreateCategory} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah Kategori
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Nama Kategori</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length > 0 ? (
                categories.map((category, index) => (
                  <TableRow key={category.id || index} className="border-white/5">
                    <TableCell className="py-3 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-3 text-sm font-semibold text-white">{category.name}</TableCell>
                    <TableCell className="py-3">
                      <Badge className={`rounded-sm ${category.is_active !== false ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"}`}>
                        {category.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEditCategory(category)}
                        className="mr-1 rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(category.id)}
                        className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada kategori titipan.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <div className="flex flex-col gap-4 border-b border-white/10 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-semibold text-white">Daftar Produk Titipan</h3>
              <p className="mt-1 text-sm text-zinc-500">Produk terbaru otomatis muncul di urutan paling atas.</p>
            </div>
            <div className="w-full md:w-64">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Filter Status</Label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="mt-2 h-11 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm font-semibold text-white outline-none transition-colors focus:border-[#D4AF37]/60 [&_option]:bg-white [&_option]:text-black"
              >
                <option value="all">Semua Produk</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>Produk</TableHead>
                <TableHead>Penitip</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Harga Jual</TableHead>
                <TableHead>Stok</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-zinc-500">Memuat...</TableCell>
                </TableRow>
              ) : visibleConsignmentProducts.length > 0 ? (
                visibleConsignmentProducts.map((product) => (
                  <TableRow key={product.id} className="border-white/5">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={resolveImageUrl(product.image)} alt={product.name} className="h-11 w-11 rounded-sm object-cover ring-1 ring-white/10" />
                        <div>
                          <p className="font-medium text-white">{product.name}</p>
                          <p className="text-xs text-zinc-500">Produk titipan</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-zinc-200">{product.consignor_name || "-"}</p>
                      <p className="text-xs text-zinc-500">{product.consignor_phone || "-"}</p>
                    </TableCell>
                    <TableCell className="text-zinc-300">{categoryMap[product.category] || product.category}</TableCell>
                    <TableCell className="text-zinc-300">{formatRupiah(product.price)}</TableCell>
                    <TableCell className="text-zinc-300">{Number(product.stock || 0)}</TableCell>
                    <TableCell>
                      {product.consignment_settlement_saved ? (
                        <span
                          className="inline-flex rounded-sm border border-zinc-500/30 bg-zinc-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-400"
                          title="Produk sudah direkap setoran"
                        >
                          Inactive
                        </span>
                      ) : (
                        <span
                          className={`inline-flex rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            product.is_active !== false
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                              : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                          }`}
                        >
                          {product.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        type="button"
                        onClick={() => openSettlementModal(product)}
                        className="mr-1 rounded-sm p-2 text-zinc-400 transition-colors hover:bg-[#D4AF37]/10 hover:text-[#F3D77A]"
                        title="Rekap setoran"
                      >
                        <Calculator className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-zinc-500">
                    {consignmentProducts.length > 0
                      ? "Tidak ada produk titipan untuk filter ini."
                      : "Belum ada produk titipan."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden rounded-sm border-white/10 bg-zinc-900 p-0 text-white">
            <DialogHeader className="shrink-0">
              <div className="border-b border-white/10 px-6 py-5">
                <DialogTitle>Tambah Produk Titipan</DialogTitle>
                <p className="mt-1 text-sm text-zinc-500">
                  Titipan baru akan langsung masuk ke daftar titipan, tidak masuk Payment Request.
                </p>
              </div>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <div className="mb-4 flex flex-col justify-between gap-2 md:flex-row md:items-start">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">1. Pilih Produk</p>
                    <p className="mt-1 text-sm text-zinc-500">Pakai data produk lama atau ketik produk titipan baru.</p>
                  </div>
                  {selectedExistingProduct ? (
                    <Badge className="w-fit border-cyan-500/30 bg-cyan-500/10 text-cyan-200">Data lama dipakai ulang</Badge>
                  ) : (
                    <Badge className="w-fit border-white/10 bg-white/5 text-zinc-300">Produk baru</Badge>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-[1fr_0.9fr]">
                  <div className="space-y-2">
                    <Label>Produk Titipan Lama</Label>
                    <select
                      value={selectedExistingProductId}
                      onChange={(e) => handleProductNameSelect(e.target.value)}
                      className="h-11 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none [&_option]:bg-white [&_option]:text-black"
                    >
                      <option value="">Tidak pakai produk lama</option>
                      {uniqueConsignmentProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-zinc-500">Boleh memilih produk nonaktif untuk membuat titipan baru dengan nama yang sama.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="h-11 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none [&_option]:bg-white [&_option]:text-black"
                      required
                    >
                      <option value="">Pilih kategori</option>
                      {formCategoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}{category.is_active === false ? " (Non Aktif)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="space-y-2">
                    <Label>Nama Produk</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Contoh: Susu Protein Vanilla"
                      required
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">2. Data Penitip</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Nama Penitip</Label>
                    <Input value={formData.consignor_name} onChange={(e) => setFormData({ ...formData, consignor_name: e.target.value })} placeholder="Nama orang/toko penitip" required />
                  </div>
                  <div className="space-y-2">
                    <Label>No HP Penitip</Label>
                    <Input value={formData.consignor_phone} onChange={(e) => setFormData({ ...formData, consignor_phone: e.target.value })} placeholder="Opsional" />
                  </div>
                </div>
              </section>

              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">3. Titipan Masuk</p>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Harga Jual / Pcs</Label>
                    <Input type="number" min={0} value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="Contoh: 25000" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Stok Masuk</Label>
                    <Input type="number" min={0} value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} placeholder="Jumlah titipan" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Foto Produk</Label>
                    <Input type="file" accept="image/*" onChange={(e) => setSelectedImageFile(e.target.files?.[0] || null)} required={!formData.image} />
                    <p className="text-xs text-zinc-500">
                      {formData.image && !selectedImageFile ? "Opsional, foto lama sudah dipakai." : "Wajib untuk produk baru."}
                    </p>
                  </div>
                </div>
              </section>

              <div className="space-y-2">
                <Label>Catatan</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Opsional, contoh: batch titipan kedua / warna baru" />
              </div>
              </div>
              <div className="flex shrink-0 gap-3 border-t border-white/10 bg-zinc-900 px-6 py-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 rounded-sm border-white/10 text-white hover:bg-zinc-800">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {submitting ? "Menyimpan..." : "Simpan Titipan Baru"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
          <DialogContent className="max-w-lg rounded-sm border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>{editingCategory ? "Edit Kategori Titipan" : "Tambah Kategori Titipan"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nama Kategori</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <label className="flex items-center justify-between rounded-sm border border-white/10 bg-black/20 p-3 text-sm text-zinc-200">
                Active
                <input
                  type="checkbox"
                  checked={categoryForm.is_active}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              </label>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCategoryModalOpen(false)} className="flex-1 rounded-sm border-white/10 text-white hover:bg-zinc-800">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {editingCategory ? "Update Kategori" : "Simpan Kategori"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isSettlementModalOpen} onOpenChange={setIsSettlementModalOpen}>
          <DialogContent className="max-w-3xl rounded-sm border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>Rekap Setoran Produk Titipan</DialogTitle>
            </DialogHeader>
            {settlementLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                  <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                    <div className="flex items-center gap-3">
                      {settlementProduct.image ? (
                        <img
                          src={resolveImageUrl(settlementProduct.image)}
                          alt={settlementProduct.name}
                          className="h-14 w-14 rounded-sm object-cover ring-1 ring-white/10"
                        />
                      ) : null}
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Produk titipan</p>
                        <p className="font-semibold text-white">{settlementProduct.name || "-"}</p>
                        <p className="text-xs text-zinc-500">{settlementProduct.consignor_name || "Penitip"} - {settlementProduct.consignor_phone || "-"}</p>
                      </div>
                    </div>
                    <Badge className={settlementSaved ? "w-fit border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "w-fit border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A]"}>
                      {settlementSaved ? "Rekap Tersimpan" : "Belum Direkap"}
                    </Badge>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/10 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-emerald-300">Terjual</p>
                    <p className="mt-2 text-3xl font-semibold text-white">{settlementSoldQuantity} pcs</p>
                    <p className="mt-1 text-xs text-zinc-500">Sudah dibeli customer.</p>
                  </div>
                  <div className="rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-[#F3D77A]">Dikembalikan</p>
                    <p className="mt-2 text-3xl font-semibold text-[#F3D77A]">
                      {(settlementSaved ? settlementReturnedStock : settlementRemainingStock)} pcs
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">Sisa stok ke penitip.</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-zinc-950 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">Stok Aktif</p>
                    <p className="mt-2 text-3xl font-semibold text-white">{settlementRemainingStock} pcs</p>
                    <p className="mt-1 text-xs text-zinc-500">Masih tampil di POS.</p>
                  </div>
                </div>

                <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Perhitungan Setoran</p>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Harga Jual / Pcs</Label>
                      <Input value={formatRupiah(settlementProduct.price || 0)} readOnly className="border-white/10 bg-zinc-950 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label>Harga Beli / Pcs</Label>
                      <Input
                        type="number"
                        min="0"
                        value={purchasePricePerPcs}
                        onChange={(e) => setPurchasePricePerPcs(e.target.value)}
                        className="border-white/10 bg-zinc-950 text-white"
                        placeholder="Masukkan harga beli per pcs"
                        disabled={settlementSaved}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status Rekap</Label>
                      <div className="flex h-10 items-center rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-zinc-300">
                        {settlementSaved ? "Sudah disimpan" : "Belum disimpan"}
                      </div>
                    </div>
                  </div>
                  {!settlementSaved ? (
                    <div className="mt-4 space-y-2">
                      <Label>Bukti Foto / Invoice</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(event) => setSettlementEvidenceFile(event.target.files?.[0] || null)}
                        className="border-white/10 bg-zinc-950 text-white file:mr-3 file:rounded-sm file:border-0 file:bg-[#D4AF37] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-black"
                      />
                      <p className="text-xs text-zinc-500">
                        {settlementEvidenceFile
                          ? `File dipilih: ${settlementEvidenceFile.name}`
                          : "Opsional. Upload bukti pembayaran, nota, atau invoice setoran."}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-sm border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/15 to-black/20 p-5">
                  <div className="flex flex-col justify-between gap-3 md:flex-row md:items-end">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#F3D77A]">Uang Dibayarkan ke Penitip</p>
                      <p className="mt-2 text-3xl font-semibold text-white">{formatRupiah(settlementPayable)}</p>
                    </div>
                    <p className="text-sm text-zinc-400">
                      {settlementSoldQuantity} pcs terjual x {formatRupiah(settlementPurchasePrice)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 border-t border-white/10 pt-4 md:flex-row md:items-center md:justify-between">
                  {settlementSaved ? (
                    <p className="text-sm text-emerald-300">Rekap tersimpan. Produk nonaktif dan stok aktif menjadi 0.</p>
                  ) : (
                    <p className="text-sm text-zinc-500">Pastikan harga beli per pcs sudah benar sebelum menyimpan rekap.</p>
                  )}
                  <div className="flex gap-3">
                    {!settlementSaved ? (
                      <Button
                        type="button"
                        onClick={saveSettlement}
                        disabled={settlementSaving}
                        className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                      >
                        {settlementSaving ? "Menyimpan..." : "Simpan Rekap"}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsSettlementModalOpen(false)}
                      className="rounded-sm border-white/10 text-white hover:bg-zinc-800"
                    >
                      Tutup
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
